import type { useGT } from 'gt-react';
import { FORECAST_FACTOR_MAX, FORECAST_FACTOR_MIN } from '@erp/shared-types';
import type { ForecastFactorRow } from 'src/api/rpc/hooks/procurement/useForecastFactors';

// Everything the growth/shrinkage factor list and form decide about a row or an
// input before it renders: which status a row is in, how it reads, how it is
// styled, and how a buyer's percent becomes the stored multiplier. Kept out of
// the components so the rules are unit-testable without a DOM.

type Translate = ReturnType<typeof useGT>;

function hasStarted(o: ForecastFactorRow, now: Date): boolean {
  return new Date(o.startsAt).getTime() <= now.getTime();
}

/**
 * The row scaling the current month: the newest active row whose window has
 * started. `overrides` arrives newest first, which is the resolver's
 * last-writer-wins order where windows overlap. The list endpoint's `active`
 * is the newest active row regardless of start, so a factor scheduled for next
 * year would read as the headline while an older row did the actual scaling
 * this month; the headline has to be judged against the clock.
 */
export function inEffectFactor(
  overrides: readonly ForecastFactorRow[],
  now: Date,
): ForecastFactorRow | null {
  return overrides.find((o) => o.isActive && hasStarted(o, now)) ?? null;
}

export type FactorStatus =
  'throughout' | 'partial' | 'overridden' | 'expired' | 'replaced' | 'revoked';

export function statusOf(
  o: ForecastFactorRow,
  usage: 'throughout' | 'partial' | 'overridden',
  replacedIds: ReadonlySet<string>,
): FactorStatus {
  if (o.deactivatedAt) return replacedIds.has(o.id) ? 'replaced' : 'revoked';
  return o.isActive ? usage : 'expired';
}

/** The buyer types a percent ("15", "-10", "12.5"); null while empty or not a number. */
export function parsePercent(raw: string): number | null {
  const t = raw.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/**
 * `+15` becomes `1.15`. The percent is read at one decimal first, which is the
 * form's step and what every surface prints (`formatFactorPercent`), so a typed
 * `12.34` stores `1.123` and reads back as `12.3` instead of prefilling an edit
 * with a value the save then silently changes.
 */
export function percentToFactor(pct: number): number {
  const tenths = Math.round(pct * 10) / 10;
  return Math.round((1 + tenths / 100) * 10000) / 10000;
}

/** The inverse, for prefilling an edit: `1.15` becomes `15`. */
export function factorToPercent(factor: number): number {
  return Math.round((factor - 1) * 1000) / 10;
}

// Same rule as the table CHECK and the contract: within the bounds and not the
// no-op multiplier of exactly 1.
export function factorInRange(factor: number): boolean {
  return (
    factor >= FORECAST_FACTOR_MIN &&
    factor <= FORECAST_FACTOR_MAX &&
    factor !== 1
  );
}

// Only rows that apply are at full strength; a struck-through row is one that
// was taken out of service rather than one that simply lapsed.
export function factorTextClasses(status: FactorStatus): string {
  const applying = status === 'throughout' || status === 'partial';
  const dim = applying ? '' : 'text-gray-400';
  const struck =
    status === 'replaced' || status === 'revoked' ? 'line-through' : '';
  return ['font-medium tabular-nums', dim, struck]
    .filter((part) => part !== '')
    .join(' ');
}

export function statusLabel(status: FactorStatus, gt: Translate): string {
  switch (status) {
    case 'throughout':
      return gt('Applies throughout', {
        context: 'growth factor remaining window status',
      });
    case 'partial':
      return gt('Applies partly', {
        context: 'growth factor remaining window status',
      });
    case 'overridden':
      return gt('Fully overridden', {
        context: 'growth factor remaining window status',
      });
    case 'expired':
      return gt('expired', { context: 'growth factor status badge' });
    case 'replaced':
      return gt('replaced', { context: 'growth factor status badge' });
    case 'revoked':
      return gt('revoked', { context: 'growth factor status badge' });
  }
}

export function statusBadgeClasses(status: FactorStatus): string {
  if (status === 'throughout') return 'bg-green-100 text-green-700';
  if (status === 'partial' || status === 'overridden') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'text-gray-400';
}

/** Every status that is actually applying, which reads as a filled badge. */
export function isApplying(status: FactorStatus): boolean {
  return status === 'throughout' || status === 'partial';
}
